import type { CameraLike, CameraOptions } from './types';
import { Vec3, type Vec3Like } from './vector';

export interface Quaternion {
  x: number;
  y: number;
  z: number;
  w: number;
}

export interface ProjectPointByCameraOptions {
  width: number;
  height: number;
  overscan?: number;
}

export interface CameraProjectedPoint {
  x: number;
  y: number;
  distance: number;
  visible: boolean;
}

export class Camera {
  readonly position: Vec3;
  readonly target: Vec3;
  readonly fov?: number;
  readonly near?: number;
  readonly far?: number;

  static from(value: CameraLike): Camera {
    return value instanceof Camera ? value : new Camera(value);
  }

  constructor(options: CameraLike) {
    this.position = Vec3.from(options.position);
    this.target = Vec3.from(options.target);
    this.fov = options.fov;
    this.near = options.near;
    this.far = options.far;
  }

  direction(fallback: Vec3Like = Vec3.FORWARD): Vec3 {
    return this.target.sub(this.position).normalize(fallback);
  }

  lookAtQuaternion(): Quaternion {
    return lookAtQuaternion(this.position, this.target);
  }

  project(point: Vec3Like, options: ProjectPointByCameraOptions): CameraProjectedPoint {
    return projectPoint(point, this, options);
  }

  toOptions(): CameraOptions {
    return {
      position: this.position.toObject(),
      target: this.target.toObject(),
      fov: this.fov,
      near: this.near,
      far: this.far,
    };
  }

  toJSON(): CameraOptions {
    return this.toOptions();
  }
}

export function direction(camera: CameraLike, fallback: Vec3Like = Vec3.FORWARD): Vec3 {
  return Camera.from(camera).direction(fallback);
}

export function lookAtQuaternion(eye: Vec3Like, target: Vec3Like): Quaternion {
  const forward = Vec3.from(target).sub(eye).normalize(Vec3.FORWARD);
  const worldUp = Math.abs(forward.y) > 0.999 ? new Vec3(0, 0, 1) : Vec3.UP;
  const right = forward.cross(worldUp).normalize(new Vec3(1, 0, 0));
  const up = right.cross(forward);

  const m00 = right.x;
  const m01 = up.x;
  const m02 = -forward.x;
  const m10 = right.y;
  const m11 = up.y;
  const m12 = -forward.y;
  const m20 = right.z;
  const m21 = up.z;
  const m22 = -forward.z;

  const trace = m00 + m11 + m22;
  let qx: number;
  let qy: number;
  let qz: number;
  let qw: number;

  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1.0);
    qw = 0.25 / s;
    qx = (m21 - m12) * s;
    qy = (m02 - m20) * s;
    qz = (m10 - m01) * s;
  } else if (m00 > m11 && m00 > m22) {
    const s = 2.0 * Math.sqrt(1.0 + m00 - m11 - m22);
    qw = (m21 - m12) / s;
    qx = 0.25 * s;
    qy = (m01 + m10) / s;
    qz = (m02 + m20) / s;
  } else if (m11 > m22) {
    const s = 2.0 * Math.sqrt(1.0 + m11 - m00 - m22);
    qw = (m02 - m20) / s;
    qx = (m01 + m10) / s;
    qy = 0.25 * s;
    qz = (m12 + m21) / s;
  } else {
    const s = 2.0 * Math.sqrt(1.0 + m22 - m00 - m11);
    qw = (m10 - m01) / s;
    qx = (m02 + m20) / s;
    qy = (m12 + m21) / s;
    qz = 0.25 * s;
  }

  const mag = Math.sqrt(qx * qx + qy * qy + qz * qz + qw * qw) || 1;
  return { x: qx / mag, y: qy / mag, z: qz / mag, w: qw / mag };
}

export function projectPoint(
  point: Vec3Like,
  camera: CameraLike,
  options: ProjectPointByCameraOptions,
): CameraProjectedPoint {
  const normalizedCamera = Camera.from(camera);
  const forward = normalizedCamera.direction(Vec3.FORWARD);
  const worldUp = Math.abs(forward.y) > 0.96 ? new Vec3(0, 0, 1) : Vec3.UP;
  const right = forward.cross(worldUp).normalize(new Vec3(1, 0, 0));
  const up = right.cross(forward).normalize(Vec3.UP);
  const rel = Vec3.from(point).sub(normalizedCamera.position);
  const x = rel.dot(right);
  const y = rel.dot(up);
  const z = rel.dot(forward);
  const distance = rel.length();

  if (z <= 0.01 || options.width <= 0 || options.height <= 0) {
    return { x: 0, y: 0, distance, visible: false };
  }

  const fov = ((normalizedCamera.fov ?? 60) * Math.PI) / 180;
  const halfHeight = Math.tan(fov / 2) * z;
  const halfWidth = halfHeight * (options.width / options.height);
  const ndcX = x / halfWidth;
  const ndcY = y / halfHeight;
  const overscan = options.overscan ?? 1;

  return {
    x: ((ndcX + 1) / 2) * options.width,
    y: ((1 - ndcY) / 2) * options.height,
    distance,
    visible: Math.abs(ndcX) <= overscan && Math.abs(ndcY) <= overscan,
  };
}
